import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { httpGet } from '../../../components/utils';
import { JitConfigurations } from '../types/JitTypes';

interface PrivilegedAccountsContextProperties {
    jitSynced: boolean;
    rulesSyncStatus: boolean;
    jitConfig?: JitConfigurations;
    updatePoliciesStatus?: () => Promise<void>;
}

const defaultPrivilegedAccountsContext: PrivilegedAccountsContextProperties = {
    jitSynced: false,
    rulesSyncStatus: false,
    jitConfig: undefined,
    updatePoliciesStatus: async () => {}
};

export const PrivilegedAccountContext = createContext<PrivilegedAccountsContextProperties>(
    defaultPrivilegedAccountsContext
);

export const PrivilegedAccountsContextProvider = ({ children }) => {
    const [jitSynced, setJitSynced] = useState<boolean>(defaultPrivilegedAccountsContext.jitSynced);
    const [rulesSyncStatus, setRulesSyncStatus] = useState<boolean>(defaultPrivilegedAccountsContext.rulesSyncStatus);
    const [jitConfig, setJitConfig] = useState<JitConfigurations>();

    const updatePoliciesStatus = useCallback(async () => {
        try {
            const responseJit: { data: { isJitSynced: boolean } | null } = await httpGet('/jit/rulesStatus');
            setJitSynced(responseJit?.data?.isJitSynced || false);

            const responseRules: { data: { RulesSynced: boolean } | null } = await httpGet('/policy/rulesSynced');
            setRulesSyncStatus(responseRules?.data?.RulesSynced || false);
        } catch (error) {
            console.error('Error fetching JIT status:', error);
        }
    }, []);

    useEffect(() => {
        const shortPullData = () => {
            updatePoliciesStatus();
            setTimeout(shortPullData, 10000); // Repeat every 10 seconds
        };

        shortPullData();
    }, []);

    useEffect(() => {
        const fetchJitConfig = async () => {
            try {
                const response: { data: JitConfigurations | null } = await httpGet('/jit/config');
                if (!response.data) {
                    throw new Error('No JIT configuration data received');
                }
                setJitConfig(response.data);
            } catch (error) {
                console.error('Error fetching JIT config:', error);
            }
        };

        fetchJitConfig();
    }, []);

    return (
        <PrivilegedAccountContext.Provider value={{ jitSynced, rulesSyncStatus, jitConfig, updatePoliciesStatus }}>
            {children}
        </PrivilegedAccountContext.Provider>
    );
};

export const usePrivilegedAccountContext = (): PrivilegedAccountsContextProperties =>
    useContext(PrivilegedAccountContext);
